import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: "easeOut" } }),
};

const festivals = [
  { name: "Miami Music Week", detail: "Miami, FL" },
  { name: "Tomorrowland Academy", detail: "Boom, Belgium" },
  { name: "Destination Festival", detail: "Laytonville, CA" },
  { name: "The Midway", detail: "San Francisco, CA" },
  { name: "1015 Folsom", detail: "San Francisco, CA" },
  { name: "Halcyon", detail: "San Francisco, CA" },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-20">
      <SEO title="About / EPK" description="Electronic press kit for Phöön — house and tech house DJ/producer. Bio, press photos, and booking information." path="/about" />
      {/* Hero — Full-width image underlay with ABOUT title + Pull Quote overlay */}
      <section className="relative w-full min-h-[70vh] md:min-h-[80vh] flex items-end overflow-hidden">
        {/* Background Image */}
        <img
          src="/api/files/phoon-site/uploads/b9303b41-6da0-4ef9-9246-a20700c1a177.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#080810] via-[#080810]/60 to-transparent" />

        {/* Text overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24 w-full">
          <motion.h1
            data-testid="about-page-title"
            className="font-['Black_Han_Sans'] text-5xl md:text-7xl text-[#F4EFE6] uppercase mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            ABOUT
          </motion.h1>

          <motion.blockquote
            data-testid="pull-quote"
            className="max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <p className="font-['Black_Han_Sans'] text-3xl md:text-5xl lg:text-6xl text-[#F4EFE6] leading-tight uppercase pull-quote-stroke">
              &ldquo;Not just to move dance floors — but to create <span className="text-[#FF5500]">Visceral</span> experiences.&rdquo;
            </p>
          </motion.blockquote>
        </div>
      </section>

      {/* Artist Photo + Bio */}
      <section data-testid="bio-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
            <motion.div
              className="relative border border-[#FF5500]/20"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            >
              <img
                data-testid="artist-photo"
                src="/api/files/phoon-site/uploads/31b2442b-1c8d-418f-bcb9-36361090a4d1.JPG"
                alt="Phöön — DJ / Producer"
                className="w-full aspect-[3/4] object-cover transition-all duration-700"
              />
            </motion.div>

            <motion.div
              className="flex flex-col justify-center"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} custom={1}
            >
              <div className="space-y-6">
                <p className="text-lg md:text-xl leading-relaxed text-[#F4EFE6]/85">Emerging from the vibrant underground of <strong className="text-[#F4EFE6]">New York City</strong>, Phöön is an eclectic artist whose sound is shaped by deep-rooted cultural influences and years immersed in nightlife across coasts. After relocating to the <strong className="text-[#F4EFE6]">Bay Area</strong>, his artistic journey took flight, sparked by a close mentor's guidance. Since then, Phöön has brought his immersive sound to <strong className="text-[#F4EFE6]">San Francisco, Los Angeles, New York, Toronto</strong>, and beyond, including an appearance at Belgium's renowned <strong className="text-[#F4EFE6]">Tomorrowland Festival</strong> as part of the <strong className="text-[#F4EFE6]">Tomorrowland Academy</strong> cohort.</p>

                <p className="text-lg md:text-xl leading-relaxed text-[#F4EFE6]/85">He's performed alongside artists like <strong className="text-[#F4EFE6]">Black Coffee, Flowdan, CID, Hills, MK, Colette, Dillon Nathaniel</strong>, and <strong className="text-[#F4EFE6]">Kinahau</strong>. Blending the soul of House and the edge of Tech House, Phöön's music bridges emotional depth with undeniable energy. Each performance is an extension of his artistic lens, crafted not just to move dance floors, but to create visceral, connective experiences.</p>

                <p className="text-lg md:text-xl leading-relaxed text-[#F4EFE6]/85">In 2024, he launched <strong className="text-[#F4EFE6]">Neighborhood Watch Radio</strong>, a San Francisco based collective designed to unite DJs, artists, and the broader community. The concept is rooted in providing a platform for artistic expression while giving back and creating lasting, impactful experiences.</p>

                <p className="text-lg md:text-xl leading-relaxed text-[#F4EFE6]/85">In 2026, he signed his hit single with <strong className="text-[#F4EFE6]">Control and Freak Records</strong> called <strong className="text-[#F4EFE6]">"Bad Lil Thing"</strong> which has been featured on numerous Spotify playlists and supported from artists such as <strong className="text-[#F4EFE6]">Dark Heart</strong> and <strong className="text-[#F4EFE6]">Haylee Wood</strong>.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Festivals & Venues */}
      <section data-testid="festivals-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Festivals & Venues
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {festivals.map((venue, i) => (
              <motion.div
                key={`fest-${venue.name}-${i}`}
                data-testid={`festival-item-${i}`}
                className="flex items-baseline justify-between py-4 border-b border-[#FF5500]/15 group"
                initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeInUp}
              >
                <span className="font-['Black_Han_Sans'] text-2xl md:text-3xl text-[#F4EFE6]/70 group-hover:text-[#F4EFE6] transition-colors duration-300 uppercase">
                  {venue.name}
                </span>
                {venue.detail && (
                  <span className="font-mono-label text-[10px] text-[#F4EFE6]/60 ml-4 shrink-0">{venue.detail}</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Photos */}
      <section data-testid="press-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            className="font-mono-label text-base text-[#FF5500] mb-10"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            Press Photos
          </motion.p>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          >
            <img src="/api/files/phoon-site/uploads/898d89c2-f016-46c0-bcf2-3fe527530253.jpg" alt="Press photo 1" className="w-full aspect-video object-cover grayscale hover:grayscale-0 transition-all duration-700 border border-[#FF5500]/10" />
            <img src="/api/files/phoon-site/uploads/e446c8dc-47b4-471c-9403-aa354ba3d656.jpg" alt="Press photo 2" className="w-full aspect-video object-cover grayscale hover:grayscale-0 transition-all duration-700 border border-[#FF5500]/10" />
            <img src="/api/files/phoon-site/uploads/ec6def02-88a3-4fb4-a9fd-cca2aa353d1b.jpg" alt="Press photo 3" className="w-full aspect-video object-cover grayscale hover:grayscale-0 transition-all duration-700 border border-[#FF5500]/10" />
          </motion.div>

          <motion.div className="mt-8 flex flex-wrap gap-4" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <a
              href="https://phoon-epk.netlify.app/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-epk"
              className="inline-block border border-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-8 py-3 hover:bg-[#FF5500] transition-all duration-300"
            >
              View EPK
            </a>
            <a
              href="https://drive.google.com/drive/folders/1SDYFKQ09n9vMjatek-a3PxNDC4gMmaOj?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-press-photos"
              className="inline-block border border-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-8 py-3 hover:bg-[#FF5500] transition-all duration-300"
            >
              Download Press Photos
            </a>
          </motion.div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="font-mono-label text-base text-[#FF5500] mb-6">Booking</p>
            <a
              href="mailto:phoonforbookings@gmail.com"
              data-testid="booking-email-link"
              className="font-['Black_Han_Sans'] text-xl sm:text-2xl md:text-4xl text-[#F4EFE6] hover:text-[#FF5500] transition-colors duration-300 uppercase break-all"
            >
              phoonforbookings@gmail.com
            </a>
            <div className="mt-8">
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-block bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-xl uppercase tracking-wider px-10 py-3 hover:bg-[#FF5500]/80 transition-all duration-300"
              >
                Get In Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
